/**
 * Mirror of `functions/src/jobs/lib/reviewGate.ts`, line for line. The server is
 * the boundary that actually holds; this copy exists so the button can say WHY
 * it is disabled before the dispatcher clicks it. Keep the two in step.
 *
 * When may a dispatcher confirm an order held in review?
 *
 * Confirming is what puts the work in the calendar and tells the client it is
 * booked, so it has to mean someone will actually turn up. An empty confirmed
 * order is worse than an unconfirmed one: it silences the review queue, leaves
 * the schedule board with an order and no rows, and hands the client a promise
 * with nobody behind it.
 *
 * Two orders are exempt, both because demanding a roster would strand them:
 *
 *   PARTNER-AWARDED. The partner fills it through their own portal on their own
 *   timetable, and the award IS the commitment — RM24 has a counterparty on the
 *   hook. Waiting on RM24's own assignments would make partner orders
 *   permanently unconfirmable.
 *
 *   UNPAID PREPAY. `assignWorker` refuses to staff a prepay order until the
 *   proforma is paid (EPIC 29) — deliberately, so RM24 does not commit people
 *   before the money. Requiring staffing here as well would deadlock the order
 *   against itself: unstaffable until paid, unconfirmable until staffed. The
 *   promise is kept by the payment gate instead, which cancels the order if the
 *   prepayment never arrives.
 */

import { jobCoverage, prepayCleared, segmentsOf, workerIdsOf } from '@rendimees24/shared-types';
import type { JobShift } from '@rendimees24/shared-types';

import { coverageSummary, isFullyStaffed } from './coverage';

export interface ReviewGateJob {
  review?: { reason?: string };
  requirements?: {
    workerCount?: number;
    startAt?: string;
    endAt?: string;
    shift?: JobShift | null;
    location?: { addressRef?: { provider?: string } | null } | null;
  };
  assignedWorkerIds?: string[];
  assignments?: Array<{ id?: string; workerId: string; startAt?: string; endAt?: string }> | null;
  sourcing?: { awardedProviderId?: string };
  payment?: { mode?: string };
  paymentStatus?: string | null;
  prepaymentWaiver?: { decidedBy: string; decidedAt: string } | null;
}

export type ReviewGateVerdict =
  | { allowed: true; via: 'staffed' | 'partner' | 'prepay' | 'admin_approval' }
  | {
      allowed: false;
      /** People on the thinnest shift (fewest at once), not distinct names. */
      assigned: number;
      required: number;
      /** EPIC 56: shifts still short of people (absent when the order has no schedule). */
      shortShifts?: number;
      /** PB-15 — what holds it when it is not the headcount. */
      blocker?: 'address';
    };

/** Decide whether this order may be confirmed as it stands. */
export function canConfirmReview(job: ReviewGateJob): ReviewGateVerdict {
  // EPIC 56 — staffed means every shift has its places filled at once, not
  // that the roster has as many names as places: two people on Mon–Tue leave
  // Thursday empty, and two half-days back to back fill one place. Mirrors
  // `staffedSummary` in functions/src/jobs/lib/segmentPlan.ts.
  const cov = coverageSummary(job);
  const required = Math.max(1, job.requirements?.workerCount ?? 1);
  const enough = isFullyStaffed(job);
  const assigned =
    cov.totalShifts === 0
      ? workerIdsOf(segmentsOf(job)).length
      : Math.min(...jobCoverage(job).days.map((d) => d.covered));
  const shortShifts = cov.totalShifts > 0 ? { shortShifts: cov.shortShifts } : {};
  // PB-15 — an address the client typed by hand is a request, not a place:
  // whatever else the review is about, the order is not promised (or its
  // terms and prepayment set) until RM24 has confirmed where the work is.
  if (job.requirements?.location?.addressRef?.provider === 'unverified') {
    return { allowed: false, assigned, required, blocker: 'address' };
  }

  // Commercial approval must precede staffing for a non-contract order.
  if (job.review?.reason === 'non_contract' || job.review?.reason === 'payment_risk')
    return { allowed: true, via: 'admin_approval' };
  if (job.sourcing?.awardedProviderId) return { allowed: true, via: 'partner' };
  if (!prepayCleared(job)) {
    return { allowed: true, via: 'prepay' };
  }

  return enough
    ? { allowed: true, via: 'staffed' }
    : { allowed: false, assigned, required, ...shortShifts };
}
